/**
 * useReactionBurst — TG-style burst overlay при ДОБАВЛЕНИИ реакции (P2#10e).
 *
 * Управляет:
 *  - burst state (emoji + key для force-remount overlay при множественных
 *    burst подряд)
 *  - triggerBurst(emoji) — установить и auto-clear через 850мс
 *  - isAddingReaction(messageId, emoji) — читает react-query кэш чтобы понять
 *    добавляем ли мы реакцию или снимаем (не файрить burst при toggle-OFF)
 *
 * Render burst-overlay через createPortal остаётся в ChatPage — hook только
 * предоставляет state и хелперы.
 */
import { useCallback, useState } from "react";
import type { QueryClient } from "@tanstack/react-query";
import type { ChatMessage } from "@/api/dashboard";

export type BurstState = { emoji: string; key: number; assetUrl: string | null } | null;

/** Маппинг emoji → TG-style animated WebP в /public/animations/reactions/.
 *  Файлы скачаны из github.com/Tarikul-Islam-Anik/Telegram-Animated-Emojis
 *  (анимированные WebP, ~500KB-1MB каждый, нативная браузерная анимация без
 *  Lottie-runtime). Эмодзи без WebP получают fallback на CSS-burst (см.
 *  ChatPage render). Расширять — просто положить новый WebP + добавить запись. */
const ANIM_ASSET_MAP: Record<string, string> = {
  "👍": "thumbs_up.webp",
  "❤️": "red_heart.webp",
  "🥰": "smiling_face_with_hearts.webp",
  "👏": "clapping_hands.webp",
  "😁": "beaming_face_with_smiling_eyes.webp",
  "🤔": "thinking_face.webp",
  "🤯": "exploding_head.webp",
  "😱": "face_screaming.webp",
  "🎉": "party_popper.webp",
  "🙏": "folded_hands.webp",
  "😢": "loudly_crying_face.webp",
  "🥴": "pleading_face.webp",
  "🐳": "spouting_whale.webp",
  "🤣": "rofl.webp",
  "😎": "cool.webp",
  "✨": "sparkles.webp",
};

function getAnimAssetUrl(emoji: string): string | null {
  const file = ANIM_ASSET_MAP[emoji];
  return file ? `/animations/reactions/${file}` : null;
}

export type UseReactionBurstParams = {
  activeTopicId: number | null;
  qc: QueryClient;
};

export function useReactionBurst(params: UseReactionBurstParams) {
  const { activeTopicId, qc } = params;
  const [burst, setBurst] = useState<BurstState>(null);

  const triggerBurst = useCallback((emoji: string) => {
    const assetUrl = getAnimAssetUrl(emoji);
    setBurst({ emoji, key: Date.now(), assetUrl });
    // WebP длится 2-3с, но overlay убираем через 1.4с — peak момент
    // анимации, а полное прокручивание раздражает. Без WebP — CSS-burst
    // 850мс достаточно.
    window.setTimeout(() => setBurst(null), assetUrl ? 1400 : 850);
  }, []);

  const isAddingReaction = useCallback((messageId: number, emoji: string): boolean => {
    if (!activeTopicId) return false;
    const cached = qc.getQueryData<ChatMessage[]>(["chat/messages", activeTopicId]) ?? [];
    const m = cached.find(x => x.id === messageId);
    if (!m) return false;
    const existing = (m.reactions ?? []).find(r => r.emoji === emoji);
    return !existing || !existing.mine;
  }, [activeTopicId, qc]);

  return { burst, triggerBurst, isAddingReaction };
}
